import { useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { VideoPlayer } from '@/components/common/VideoPlayer'
import { ImageWithFallback } from '@/components/common/ImageWithFallback'
import { useAdminSharedVideo, useAdminDeleteSharedVideo } from '../api/admin-api'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Trash2, LayoutTemplate, ExternalLink } from 'lucide-react'
import { toast } from 'sonner'
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
} from '@/components/ui/breadcrumb'

export function SharedVideoDetail() {
  const { shareId } = useParams()
  const navigate = useNavigate()
  const { data: sharedVideo, isLoading, error } = useAdminSharedVideo(shareId!)
  const deleteMutation = useAdminDeleteSharedVideo()
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  const handleDelete = async () => {
    if (!shareId) return

    try {
      await deleteMutation.mutateAsync(shareId)
      toast.success('공개 비디오가 삭제되었습니다')
      navigate('/admin/shared-videos')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '삭제에 실패했습니다')
    }
  }

  if (isLoading) {
    return <div className="text-center py-12 text-gray-500">로딩 중...</div>
  }

  if (error) {
    return <div className="text-center py-12 text-red-500">에러: {error.message}</div>
  }

  if (!sharedVideo) return null

  return (
    <div className="max-w-4xl space-y-6">
      {/* Breadcrumb */}
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/admin">어드민</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/admin/shared-videos">공개된 비디오</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>상세</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-heading tracking-tight">공개 비디오 상세</h1>
          <p className="text-caption mt-2 leading-relaxed">
            커뮤니티에 공유된 비디오의 상세 정보입니다
          </p>
        </div>
        <Button
          variant="destructive"
          onClick={() => setShowDeleteDialog(true)}
        >
          <Trash2 className="w-4 h-4 mr-2" />
          공개 삭제
        </Button>
      </div>

      {/* Video Preview */}
      <div className="bg-white rounded-lg border p-6">
        <label className="text-sm font-medium text-gray-700 block mb-4">공개 비디오</label>
        <div className="flex justify-center bg-gray-50 rounded-lg p-4">
          <VideoPlayer
            src={sharedVideo.videoUrl}
            poster={sharedVideo.thumbnailUrls?.[0] ?? sharedVideo.thumbnailUrl ?? undefined}
            controls
            className="max-w-full max-h-[500px] rounded-lg shadow-lg"
          />
        </div>
      </div>

      {/* Share Info */}
      <div className="bg-white rounded-lg border p-6 space-y-6">
        <h2 className="text-lg font-semibold text-heading tracking-tight">공유 정보</h2>
        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="text-sm font-medium text-gray-700">공유 ID</label>
            <div className="mt-1 text-gray-900 font-mono text-sm">{sharedVideo.id}</div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">공개자</label>
            <div className="mt-1 text-gray-900">{sharedVideo.userEmail ?? '-'}</div>
            <div className="text-xs text-gray-400 mt-1 font-mono">{sharedVideo.userId}</div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">비율</label>
            <div className="mt-1">
              <Badge variant="outline">{sharedVideo.ratio}</Badge>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">공개일시</label>
            <div className="mt-1 text-gray-900 text-sm">
              {new Date(sharedVideo.createdAt).toLocaleString('ko-KR')}
            </div>
          </div>

          <div className="col-span-2">
            <label className="text-sm font-medium text-gray-700">공유 URL</label>
            {(() => {
              const basePath = (import.meta.env.VITE_BASE_PATH || '').replace(/\/$/, '')
              const shareUrl = `${window.location.origin}${basePath}/share/${sharedVideo.id}`
              return (
                <div className="mt-1 flex items-center gap-2">
                  <code className="flex-1 p-2 bg-gray-50 rounded text-sm font-mono text-gray-700">
                    {shareUrl}
                  </code>
                  <a
                    href={`${basePath}/share/${sharedVideo.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:text-primary/80"
                  >
                    <ExternalLink className="w-5 h-5" />
                  </a>
                </div>
              )
            })()}
          </div>
        </div>
      </div>

      {/* Generation Info */}
      {sharedVideo.generation && (
        <div className="bg-white rounded-lg border p-6 space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-heading tracking-tight">생성 정보</h2>
            <Link
              to={`/admin/generations/${sharedVideo.generationId}/view`}
              className="text-sm text-primary hover:underline"
            >
              생성 상세 보기
            </Link>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="text-sm font-medium text-gray-700">생성일시</label>
              <div className="mt-1 text-gray-900 text-sm">
                {new Date(sharedVideo.generation.createdAt).toLocaleString('ko-KR')}
              </div>
            </div>

            {/* Template */}
            <div className="col-span-2">
              <label className="text-sm font-medium text-gray-700">템플릿</label>
              {sharedVideo.template ? (
                <div className="mt-2 flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  {sharedVideo.template.thumbnailUrl ? (
                    <ImageWithFallback
                      src={sharedVideo.template.thumbnailUrl}
                      alt={sharedVideo.template.name}
                      className="w-16 h-16 rounded-lg object-cover border"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-lg bg-gray-200 flex items-center justify-center">
                      <LayoutTemplate className="w-6 h-6 text-gray-400" />
                    </div>
                  )}
                  <div>
                    <Link
                      to={`/admin/templates/${sharedVideo.template.id}`}
                      className="font-medium text-gray-900 hover:text-primary transition-colors"
                    >
                      {sharedVideo.template.name}
                    </Link>
                    {sharedVideo.template.description && (
                      <p className="text-sm text-gray-500 mt-0.5">
                        {sharedVideo.template.description}
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="mt-1 text-gray-400">사용된 템플릿 없음</div>
              )}
            </div>
          </div>

          {/* Prompt */}
          <div>
            <label className="text-sm font-medium text-gray-700">프롬프트</label>
            <div className="mt-1 p-4 bg-gray-50 rounded-lg text-gray-900 whitespace-pre-wrap font-mono text-sm">
              {sharedVideo.generation.prompt || '-'}
            </div>
          </div>

          {/* Frame Images */}
          {sharedVideo.generation.frameImages &&
            sharedVideo.generation.frameImages.length > 0 && (
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-4">
                  프레임 이미지 ({sharedVideo.generation.frameImages.length}개)
                </label>
                <div className="grid grid-cols-2 gap-4">
                  {sharedVideo.generation.frameImages.map((frame, idx) => (
                    <div key={idx} className="bg-gray-100 rounded-lg overflow-hidden">
                      <ImageWithFallback
                        src={frame.thumbnailUrl}
                        alt={`프레임 이미지 ${idx + 1}`}
                        className="w-full h-auto object-contain"
                        fallbackClassName="h-24"
                        loading="lazy"
                      />
                      <div className="text-xs text-center py-1 text-gray-500">
                        {frame.role === 'first_frame' ? '첫 프레임' : '마지막 프레임'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>공개 비디오 삭제</DialogTitle>
            <DialogDescription className="mt-2">
              이 비디오를 커뮤니티에서 삭제하시겠습니까?
              <br />
              원본 비디오는 생성자의 히스토리에 유지됩니다.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              disabled={deleteMutation.isPending}
            >
              취소
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? '삭제 중...' : '삭제'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
